import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowDownward,
    MdArrowUpward,
    MdDelete,
    MdLock,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import ConfirmModal from '@/components/confirm-modal'
import {
    createPresenceStatus,
    deletePresenceStatus,
    isCustomColor,
    presenceBadgeProps,
    PresenceStatus,
    reorderPresenceStatuses,
    updatePresenceStatus,
} from '@/lib/api/presence/presence-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'

interface PresenceStatusManagerProps {
    statuses: PresenceStatus[]
    onChanged: () => void
}

const COLOR_OPTIONS: Array<{ value: string; label: string }> = [
    { value: 'error', label: 'Vermelho' },
    { value: 'warning', label: 'Laranja' },
    { value: 'accent', label: 'Destaque' },
    { value: 'success', label: 'Verde' },
    { value: 'info', label: 'Azul' },
    { value: 'primary', label: 'Primária' },
    { value: 'secondary', label: 'Secundária' },
    { value: 'neutral', label: 'Neutra' },
]

/** Valor do <select> que abre a paleta em vez de aplicar uma cor do tema. */
const CUSTOM_COLOR_OPTION = 'CUSTOM'

/** Cor inicial da paleta quando o controle ainda usa uma cor do tema. */
const DEFAULT_CUSTOM_COLOR = '#7c3aed'

/**
 * Seletor de cor: as cores do tema (que acompanham claro/escuro do usuário) mais
 * "Personalizado", que abre a paleta do navegador e grava o hexadecimal.
 *
 * A distinção importa: cor do tema é um papel ("erro", "sucesso") e muda de tom
 * com o tema; hexadecimal é literal e fica igual nos dois. Por isso as duas
 * convivem em vez de a paleta substituir a lista.
 */
function ColorPicker({
    value,
    disabled,
    id,
    onChange,
}: {
    value: string
    disabled?: boolean
    id?: string
    onChange: (color: string) => void
}) {
    const custom = isCustomColor(value)
    // Rascunho local: a paleta pinta o badge a cada movimento, mas só o valor
    // que ficar parado é que sobe. Sem isso, arrastar na paleta viraria um PATCH
    // por pixel percorrido; persistir só no blur perdia a escolha de quem clica
    // direto em "Adicionar" depois de escolher a cor.
    const [draft, setDraft] = useState(value)

    useEffect(() => {
        setDraft(value)
    }, [value])

    useEffect(() => {
        if (draft === value) return
        const timer = setTimeout(() => onChange(draft), 400)
        return () => clearTimeout(timer)
    }, [draft, value, onChange])

    return (
        <div className="flex items-center gap-2">
            <select
                id={id}
                className="select select-bordered select-sm w-full !rounded-lg"
                value={custom ? CUSTOM_COLOR_OPTION : value}
                disabled={disabled}
                onChange={event =>
                    onChange(
                        event.target.value === CUSTOM_COLOR_OPTION
                            ? custom
                                ? value
                                : DEFAULT_CUSTOM_COLOR
                            : event.target.value
                    )
                }
                aria-label="Cor do controle"
            >
                {COLOR_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
                <option value={CUSTOM_COLOR_OPTION}>Personalizado</option>
            </select>

            {custom && (
                <input
                    type="color"
                    className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-base-300 bg-base-100 p-1"
                    value={draft}
                    disabled={disabled}
                    onChange={event => setDraft(event.target.value)}
                    title="Escolher a cor"
                    aria-label="Cor personalizada"
                />
            )}
        </div>
    )
}

export default function PresenceStatusManager({
    statuses,
    onChanged,
}: PresenceStatusManagerProps) {
    const { canCreate, canDelete } = useResourcePermissions('bookings', 'presence')

    const [name, setName] = useState('')
    const [color, setColor] = useState('neutral')
    const [creating, setCreating] = useState(false)
    const [savingId, setSavingId] = useState<number | null>(null)
    const [statusToDelete, setStatusToDelete] = useState<PresenceStatus | null>(null)

    const handleCreate = async () => {
        if (!name.trim()) {
            toast.error('Informe o nome do controle')
            return
        }
        setCreating(true)
        const response = await createPresenceStatus({ name: name.trim(), color })
        setCreating(false)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível criar o controle')
            return
        }
        setName('')
        setColor('neutral')
        toast.success('Controle criado')
        onChanged()
    }

    const handleUpdate = async (
        status: PresenceStatus,
        data: Parameters<typeof updatePresenceStatus>[1]
    ) => {
        setSavingId(status.id)
        const response = await updatePresenceStatus(status.id, data)
        setSavingId(null)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível salvar o controle')
            return
        }
        onChanged()
    }

    const handleMove = async (index: number, direction: -1 | 1) => {
        const target = index + direction
        if (target < 0 || target >= statuses.length) return

        const ids = statuses.map(status => status.id)
        ;[ids[index], ids[target]] = [ids[target], ids[index]]

        const response = await reorderPresenceStatuses(ids)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível reordenar')
            return
        }
        onChanged()
    }

    const handleDelete = async () => {
        if (!statusToDelete) return
        setSavingId(statusToDelete.id)
        const response = await deletePresenceStatus(statusToDelete.id)
        setSavingId(null)
        setStatusToDelete(null)
        if (!response.success) {
            toast.error(response.error || 'Não foi possível excluir o controle')
            return
        }
        toast.success('Controle excluído')
        onChanged()
    }

    return (
        <div className="flex flex-col gap-5">
            {canCreate && (
                <div className="rounded-xl border border-base-200 p-4">
                    <h4 className="font-semibold">Novo controle</h4>
                    <p className="mt-1 text-xs text-base-content/60">
                        Ele entra no fim da fila; use as setas para posicioná-lo.
                    </p>

                    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
                        <div className="flex w-full flex-col gap-1.5 sm:flex-1">
                            <label
                                className="text-sm font-medium"
                                htmlFor="presence-status-name"
                            >
                                Nome
                            </label>
                            <input
                                id="presence-status-name"
                                type="text"
                                className="input input-bordered w-full !rounded-lg"
                                placeholder="Ex.: Retorno rápido"
                                value={name}
                                onChange={event => setName(event.target.value)}
                            />
                        </div>

                        <div className="flex w-full flex-col gap-1.5 sm:w-64">
                            <label
                                className="text-sm font-medium"
                                htmlFor="presence-status-color"
                            >
                                Cor
                            </label>
                            <ColorPicker
                                id="presence-status-color"
                                value={color}
                                onChange={setColor}
                            />
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary gap-2 !rounded-lg sm:w-auto"
                            disabled={creating}
                            onClick={handleCreate}
                        >
                            {creating ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdAdd className="text-lg" />
                            )}
                            Adicionar
                        </button>
                    </div>
                </div>
            )}

            <ul className="flex flex-col gap-2">
                {statuses.map((status, index) => (
                    <li
                        key={status.id}
                        className="flex flex-col gap-3 rounded-xl border border-base-200 p-3 sm:flex-row sm:items-center"
                    >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-base-200 text-sm font-semibold">
                            {status.position}
                        </span>

                        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                            <input
                                type="text"
                                className="input input-bordered input-sm !rounded-lg sm:flex-1"
                                defaultValue={status.name}
                                disabled={savingId === status.id}
                                onBlur={event => {
                                    const value = event.target.value.trim()
                                    if (value && value !== status.name) {
                                        handleUpdate(status, { name: value })
                                    }
                                }}
                                aria-label="Nome do controle"
                            />

                            <div className="sm:w-56">
                                <ColorPicker
                                    value={status.color}
                                    disabled={savingId === status.id}
                                    onChange={color =>
                                        handleUpdate(status, { color })
                                    }
                                />
                            </div>

                            <span
                                className={`badge ${presenceBadgeProps(status.color).className}`}
                                style={presenceBadgeProps(status.color).style}
                            >
                                {status.name}
                            </span>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                            <label className="label cursor-pointer gap-2">
                                <span className="label-text text-xs">Ativo</span>
                                <input
                                    type="checkbox"
                                    className="toggle toggle-sm toggle-primary"
                                    checked={status.active}
                                    disabled={savingId === status.id}
                                    onChange={event =>
                                        handleUpdate(status, {
                                            active: event.target.checked,
                                        })
                                    }
                                />
                            </label>

                            <button
                                type="button"
                                className="btn btn-ghost btn-sm !rounded-lg"
                                title="Subir"
                                disabled={index === 0}
                                onClick={() => handleMove(index, -1)}
                            >
                                <MdArrowUpward />
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm !rounded-lg"
                                title="Descer"
                                disabled={index === statuses.length - 1}
                                onClick={() => handleMove(index, 1)}
                            >
                                <MdArrowDownward />
                            </button>

                            {status.isSystem ? (
                                <span
                                    className="tooltip tooltip-left flex h-8 w-8 items-center justify-center text-base-content/40"
                                    data-tip="Controle nativo: pode ser desativado, não excluído"
                                >
                                    <MdLock />
                                </span>
                            ) : (
                                canDelete && (
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm text-error !rounded-lg"
                                        title="Excluir"
                                        onClick={() => setStatusToDelete(status)}
                                    >
                                        <MdDelete />
                                    </button>
                                )
                            )}
                        </div>
                    </li>
                ))}
            </ul>

            <ConfirmModal
                isOpen={!!statusToDelete}
                onClose={() => setStatusToDelete(null)}
                onConfirm={handleDelete}
                title="Excluir controle"
                message={`Excluir o controle "${statusToDelete?.name ?? ''}"? Ele deixa de aparecer nas listas.`}
                confirmButtonText="Excluir"
                loading={savingId === statusToDelete?.id}
            />
        </div>
    )
}
